import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { HrnbRow, HrnbCol } from '../components/controls/HrnbContainer';
import HrnbButton from '../components/controls/HrnbButton';
import HrnbAlert from '../components/controls/HrnbAlert';
import HrnbSkeleton, { SkeletonCard } from '../components/controls/HrnbSkeleton';
import { HrnbTab, HrnbTabs } from '../components/controls/HrnbTabs';
import RoundsTabs from '../components/battles/RoundsTabs';
import JoinBattleModal from '../components/battles/JoinBattleModal';
import EditBattleForm from '../components/battles/EditBattleForm';
import TrackUploader from '../components/battles/TrackUploader'; // Компонент загрузки треков
import RoundParticipantsManager from '../components/battles/RoundParticipantsManager'; // Управление участниками раунда
import InviteParticipantModal from '../components/battles/InviteParticipantModal'; // Приглашение участников
import {
  useBattleData,
  useBattleActions,
  useBattleRounds,
  useBattleRegulation,
} from './BattleDetailPage/hooks';
import {
  BattleInfoTab,
  BattleRulesTab,
  BattleParticipantsTab,
  BattleJudgesTab,
  BattleChatTab,
} from './BattleDetailPage/tabs';
import { RoundModal, RegulationModal } from './BattleDetailPage/modals';
import { checkIsUserOrganizer, checkIsRegistered } from './BattleDetailPage/utils';
import {
  TYPE_LABELS,
  STATUS_LABELS,
  STATUS_COLORS,
  VISIBILITY_LABELS,
  FORMAT_LABELS,
} from './BattleDetailPage/constants';
import { getFormatIcon } from '../utils/battleFormats';
import '../css/pages/BattleDetailPage.css';

/**
 * Страница деталей баттла
 * Рефакторинг: логика разделена на хуки, UI на компоненты
 */
export default function BattleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useContext(UserContext);

  // Состояния UI
  const [activeTab, setActiveTab] = useState('info');
  const [filterRole, setFilterRole] = useState('PARTICIPANT');
  const [filterStatus, setFilterStatus] = useState('');
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showTrackUploader, setShowTrackUploader] = useState(null); // { roundId, authorId, battlePairId } | null
  const [showParticipantsManager, setShowParticipantsManager] = useState(null); // round | null

  // Хуки
  const battleData = useBattleData();
  const battleActions = useBattleActions(id, battleData.fetchBattle, battleData.fetchParticipants, battleData.fetchRounds);
  const battleRounds = useBattleRounds(id, battleData.fetchRounds);
  const battleRegulation = useBattleRegulation(id, battleData.fetchBattle);

  // Проверки
  const isUserOrganizer = useMemo(
    () => checkIsUserOrganizer(currentUser, battleData.battle, battleData.participants),
    [currentUser, battleData.battle, battleData.participants]
  );

  const isRegistered = useMemo(
    () => checkIsRegistered(currentUser, battleData.participants),
    [currentUser, battleData.participants]
  );

  // Эффекты
  useEffect(() => {
    if (activeTab === 'rules') {
      battleData.fetchBattleRules();
      battleData.fetchBattleLimitations();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'participants') {
      battleData.fetchParticipantsForTab(filterRole, filterStatus);
    }
  }, [activeTab, filterRole, filterStatus]);

  useEffect(() => {
    if (activeTab === 'judges') {
      battleData.fetchJudges();
    }
  }, [activeTab]);

  // Деструктуризация
  const {
    battle,
    coverData,
    coverLoading,
    participants,
    rounds,
    judges,
    battleRules,
    battleLimitations,
    roundRules,
    roundLimitations,
    roundTracks,
    participantLoading,
    fetchRoundRules,
    fetchRoundLimitations,
  } = battleData;

  // Лоадер
  if (battleData.loading) {
    return (
      <div className="battle-detail-page">
        <div className="battle-detail-header">
          <HrnbSkeleton width="160px" height="36px" style={{ marginBottom: 16 }} />
          <div className="battle-detail-title-section">
            <HrnbSkeleton width="50%" height="36px" style={{ marginBottom: 12 }} />
            <div className="battle-detail-badges">
              <HrnbSkeleton width="80px" height="28px" circle />
              <HrnbSkeleton width="80px" height="28px" circle />
              <HrnbSkeleton width="80px" height="28px" circle />
            </div>
          </div>
        </div>
        <HrnbRow>
          <HrnbCol xs={12} lg={8}>
            <SkeletonCard lines={6} />
          </HrnbCol>
          <HrnbCol xs={12} lg={4}>
            <SkeletonCard lines={3} />
          </HrnbCol>
        </HrnbRow>
      </div>
    );
  }

  // Ошибка
  if (battleData.error || !battleData.battle) {
    const isAuthError = battleData.error?.includes('необходимо войти');
    return (
      <div className="battle-detail-page">
        <HrnbAlert variant="danger" onClose={() => battleData.setError(null)}>
          {battleData.error || 'Баттл не найден'}
        </HrnbAlert>
        <div className="mt-3" style={{ display: 'flex', gap: 12 }}>
          <HrnbButton onClick={() => navigate('/battles')}>
            Вернуться к списку
          </HrnbButton>
          {isAuthError && (
            <HrnbButton variant="primary" onClick={() => navigate('/login')}>
              Войти
            </HrnbButton>
          )}
        </div>
      </div>
    );
  }

  // Деструктуризация хуков
  const {
    actionLoading,
    deleteLoading,
    handleRegister,
    handleUnregister,
    handleDelete,
    handleApproveParticipant,
    handleRejectParticipant,
    handleDisqualifyParticipant,
  } = battleActions;

  const {
    isRoundModalOpen,
    editingRoundId,
    roundActionLoading,
    expandedRoundDetails,
    loadingRoundDetails,
    roundForm,
    newRoundRule,
    newRoundLimitation,
    openCreateRoundModal,
    handleCreateRoundFromTab,
    openEditRoundModal,
    closeRoundModal,
    handleRoundFormChange,
    handleCreateRound,
    handleUpdateRound,
    handleDeleteRound,
    handleUpdateRoundStatus,
    toggleRoundDetails,
    handleAddRoundRule,
    handleDeleteRoundRule,
    handleAddRoundLimitation,
    handleDeleteRoundLimitation,
    setRoundForm,
    setNewRoundRule,
    setNewRoundLimitation,
  } = battleRounds;

  const {
    isRegulationModalOpen,
    regulationActionLoading,
    regulationForm,
    openRegulationModal,
    closeRegulationModal,
    handleRegulationFormChange,
    handleAddRuleField,
    handleUpdateRuleField,
    handleRemoveRuleField,
    handleAddLimitationField,
    handleUpdateLimitationField,
    handleRemoveLimitationField,
    handleUpdateRegulation,
  } = battleRegulation;

  return (
    <div className="battle-detail-page">
      {/* Header */}
      <div className="battle-detail-header">
        <HrnbButton variant="outline" onClick={() => navigate('/battles')} className="mb-3">
          ← Назад к списку
        </HrnbButton>
        <div className="battle-detail-title-section">
          <h1 className="battle-detail-title">{battle.title}</h1>
          <div className="battle-detail-badges">
            <span
              className="battle-detail-status-badge"
              style={{ backgroundColor: STATUS_COLORS[battle.status] || '#6c757d' }}
            >
              {STATUS_LABELS[battle.status] || battle.status}
            </span>
            <span className="battle-detail-type-badge">
              {TYPE_LABELS[battle.type] || battle.type}
            </span>
            <span className={`battle-detail-format-badge battle-detail-format-badge--${(battle.format || '').toLowerCase()}`}>
              {FORMAT_LABELS[battle.format] || battle.format}
            </span>
            <span className="battle-detail-visibility-badge">
              {VISIBILITY_LABELS[battle.visibility] || battle.visibility}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <HrnbTabs value={activeTab} onChange={setActiveTab}>
        <HrnbTab label="Информация" value="info" />
        <HrnbTab label="Правила" value="rules" />
        <HrnbTab label="Участники" value="participants" />
        <HrnbTab label="Жюри" value="judges" />
        <HrnbTab label="Раунды" value="rounds" />
        <HrnbTab label="Чат" value="chat" />
      </HrnbTabs>

      {/* Content */}
      <div className="battle-detail-content">
        {activeTab === 'info' && (
          <BattleInfoTab
            battle={battle}
            coverData={coverData}
            coverLoading={coverLoading}
            participants={participants}
            rounds={rounds}
            currentUser={currentUser}
            isUserOrganizer={isUserOrganizer}
            isRegistered={isRegistered}
            actionLoading={actionLoading}
            deleteLoading={deleteLoading}
            onRegister={() => setShowJoinModal(true)}
            onUnregister={handleUnregister}
            onDelete={handleDelete}
            onEditBattle={() => setShowEditModal(true)}
            onOpenInviteModal={() => setShowInviteModal(true)}
            navigateToParticipants={() => setActiveTab('participants')}
            setActiveTab={setActiveTab}
          />
        )}

        {activeTab === 'rules' && (
          <BattleRulesTab
            battleRules={battleRules}
            battleLimitations={battleLimitations}
            regulation={battle.regulation}
            isUserOrganizer={isUserOrganizer}
            onEditRegulation={openRegulationModal}
          />
        )}

        {activeTab === 'participants' && (
          <BattleParticipantsTab
            participants={participants}
            filterRole={filterRole}
            filterStatus={filterStatus}
            participantLoading={participantLoading}
            currentUser={currentUser}
            isUserOrganizer={isUserOrganizer}
            actionLoading={actionLoading}
            onFilterRoleChange={setFilterRole}
            onFilterStatusChange={setFilterStatus}
            onApprove={handleApproveParticipant}
            onReject={handleRejectParticipant}
            onDisqualify={handleDisqualifyParticipant}
          />
        )}

        {activeTab === 'judges' && (
          <BattleJudgesTab judges={judges} participantLoading={participantLoading} />
        )}

        {activeTab === 'rounds' && (
          <RoundsTabs
            rounds={rounds}
            battleId={id}
            isAdmin={isUserOrganizer}
            currentUser={currentUser}
            battleParticipants={participants}
            expandedDetails={expandedRoundDetails}
            loadingDetails={loadingRoundDetails}
            roundRules={roundRules}
            roundLimitations={roundLimitations}
            roundTracks={roundTracks}
            roundActionLoading={roundActionLoading}
            newRoundRule={newRoundRule}
            newRoundLimitation={newRoundLimitation}
            onCreateRound={(tabId) => handleCreateRoundFromTab(tabId, rounds)}
            onEditRound={openEditRoundModal}
            onDeleteRound={handleDeleteRound}
            onUpdateStatus={handleUpdateRoundStatus}
            onToggleDetails={(roundId) => toggleRoundDetails(roundId, fetchRoundRules, fetchRoundLimitations)}
            onAddRule={handleAddRoundRule}
            onDeleteRule={handleDeleteRoundRule}
            onAddLimitation={handleAddRoundLimitation}
            onDeleteLimitation={handleDeleteRoundLimitation}
            onNewRuleChange={(field, value) =>
              setNewRoundRule((prev) => ({ ...prev, [field]: value }))
            }
            onNewLimitationChange={(field, value) =>
              setNewRoundLimitation((prev) => ({ ...prev, [field]: value }))
            }
            onManageParticipants={(round) => setShowParticipantsManager(round)}
            onSessionClosed={() => battleData.fetchRoundTracksAll()}
            onOpenTrackUploader={(data) => setShowTrackUploader(data)}
          />
        )}

        {activeTab === 'chat' && (
          <BattleChatTab
            battle={battle}
            chatId={battle?.participantsChatId || battle?.chatId}
            isUserOrganizer={isUserOrganizer}
          />
        )}
      </div>

      {/* Modals */}
      <RoundModal
        isOpen={isRoundModalOpen}
        onClose={closeRoundModal}
        editingRoundId={editingRoundId}
        roundForm={roundForm}
        roundActionLoading={roundActionLoading}
        newRoundLimitation={battleRounds.newRoundLimitation}
        rounds={rounds}
        onFormChange={handleRoundFormChange}
        onNewLimitationChange={battleRounds.handleNewLimitationChange}
        onAddLimitation={battleRounds.handleAddLimitationToForm}
        onRemoveLimitation={battleRounds.handleRemoveLimitationFromForm}
        onCreate={handleCreateRound}
        onUpdate={handleUpdateRound}
      />

      <RegulationModal
        isOpen={isRegulationModalOpen}
        onClose={closeRegulationModal}
        regulationForm={regulationForm}
        regulationActionLoading={regulationActionLoading}
        onFormChange={handleRegulationFormChange}
        onAddRule={handleAddRuleField}
        onUpdateRule={handleUpdateRuleField}
        onRemoveRule={handleRemoveRuleField}
        onAddLimitation={handleAddLimitationField}
        onUpdateLimitation={handleUpdateLimitationField}
        onRemoveLimitation={handleRemoveLimitationField}
        onSave={handleUpdateRegulation}
      />

      <JoinBattleModal
        isOpen={showJoinModal}
        onClose={() => setShowJoinModal(false)}
        battleId={id}
        onSuccess={() => {
          battleData.fetchBattle();
          battleData.fetchParticipants();
        }}
      />

      <EditBattleForm
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        battleId={id}
        onSuccess={battleData.fetchBattle}
      />

      <InviteParticipantModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        battleId={id}
        onSuccess={() => {
          battleData.fetchBattle();
          battleData.fetchParticipants();
        }}
      />

      {/* Модальное окно загрузки трека */}
      {showTrackUploader && (
        <div className="battle-detail-modal-overlay" onClick={() => setShowTrackUploader(null)}>
          <div className="battle-detail-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="battle-detail-modal-header">
              <h3>Загрузка трека в раунд</h3>
              <button className="modal-close" onClick={() => setShowTrackUploader(null)}>
                ✕
              </button>
            </div>
            <div className="battle-detail-modal-body">
              <TrackUploader
                battleId={id}
                roundId={showTrackUploader.roundId}
                authorId={showTrackUploader.authorId}
                battlePairId={showTrackUploader.battlePairId}
                battleFormat={battle.format}
                onSuccess={(result) => {
                  console.log('Track uploaded successfully:', result);
                  const roundId = showTrackUploader.roundId;
                  setShowTrackUploader(null);
                  if (roundId) {
                    battleData.fetchRoundTracks(roundId);
                  }
                }}
                onCancel={() => setShowTrackUploader(null)}
                isAdmin={isUserOrganizer}
              />
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно управления участниками раунда */}
      {showParticipantsManager && (
        <RoundParticipantsManager
          isOpen={!!showParticipantsManager}
          onClose={() => setShowParticipantsManager(null)}
          round={showParticipantsManager}
          battleId={id}
          battleParticipants={participants || []}
          battleJudges={judges || []}
          onAddParticipant={battleRounds.handleAddRoundParticipant}
          onRemoveParticipant={battleRounds.handleRemoveRoundParticipant}
          onAddJudges={battleRounds.handleAddRoundJudges}
          loading={battleRounds.roundActionLoading}
        />
      )}
    </div>
  );
}
